/* eslint-disable */
import React, { lazy, useEffect, useRef } from "react";
import { usePrivy } from "@privy-io/react-auth";
import get from "lodash/get";
import * as Sentry from "@sentry/react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

import { config_REACT_APP_STAGE } from "src/config";
import { ROUTE_PATH, AUTH_LEVEL, APP_STAGE } from "src/constants";
import RouteErrorBoundary from "src/components/RouteErrorBoundary";
import useAccountInfo from "src/hooks/apis/useAccountInfo";
import { clearSessionCaches } from "src/hooks/useLogout";
import useAppContext from "src/pages/Core/context";

/* COMPONENTS LAZY LOADED WITH PREFETCH */
const NotFound = lazy(() => import(
  /* webpackChunkName: "core-components-deferred" */
  "src/pages/Error/NotFound"
));
const Sanctioned = lazy(() => import(
  /* webpackChunkName: "core-components-deferred" */
  "src/pages/Error/Sanctioned"
));
const UploadDocument = lazy(() => import(
  /* webpackChunkName: "core-components-deferred" */
  "src/pages/UploadDocument"
));
const InvitationCode = lazy(() => import(
  /* webpackPrefetch: true, webpackChunkName: "core-components" */
  "src/pages/InvitationCode"
));
const RootRedirect = lazy(() => import(
  /* webpackPrefetch: true, webpackChunkName: "core-components" */
  "src/pages/RootRedirect"
));

/* NAVIGATOR COMPONENTS WITH PREFETCH */
const navigatorMap = {
  "series": lazy(() => import(
    /* webpackPrefetch: true, webpackChunkName: "core-components" */
    "src/components/NavigatorSeries"
  )),
  "token": lazy(() => import(
    /* webpackPrefetch: true, webpackChunkName: "core-components" */
    "src/components/NavigatorSeries"
  )),
  "guest": lazy(() => import(
    /* webpackPrefetch: true, webpackChunkName: "core-components" */
    "src/components/NavigatorGuest"
  )),
  "grants": lazy(() => import(
    /* webpackPrefetch: true, webpackChunkName: "core-components" */
    "src/components/NavigatorGrants"
  )),
  "circle": lazy(() => import(
    /* webpackChunkName: "core-components-deferred" */
    "src/components/NavigatorSyndicate"
  )),
  "default": lazy(() => import(
    /* webpackPrefetch: true, webpackChunkName: "core-components" */
    "src/components/NavigatorSeries"
  ))
};

const allRoutes = [
  {
    exact          : true,
    path           : ROUTE_PATH.ROOT,
    componentLevel : AUTH_LEVEL.FREE,
    component      : lazy(() => import(
      /* webpackPrefetch: true, webpackChunkName: "core-components" */
      "src/pages/Root"
    )),
  },
  {
    exact          : true,
    path           : ROUTE_PATH.AUTH,
    componentLevel : AUTH_LEVEL.SIGN_IN,
    component      : lazy(() => import(
      /* webpackPrefetch: true, webpackChunkName: "core-components" */
      "src/pages/Auth"
    )),
  },
  {
    exact          : true,
    path           : ROUTE_PATH.SERIES_NOT_PUBLISHED,
    componentLevel : AUTH_LEVEL.SIGN_IN,
    component      : lazy(() => import(
      /* webpackChunkName: "core-components-deferred" */
      "src/pages/SeriesNotPublished"
    )),
  },
  {
    exact          : true,
    path           : ROUTE_PATH.INVITE_DISABLED,
    componentLevel : AUTH_LEVEL.DASHBOARD,
    component      : lazy(() => import(
      /* webpackChunkName: "core-components-deferred" */
      "src/pages/InviteDisabled"
    )),
  },
  {
    exact          : true,
    path           : ROUTE_PATH.INVALID_INVITE,
    componentLevel : AUTH_LEVEL.DASHBOARD,
    component      : lazy(() => import(
      /* webpackChunkName: "core-components-deferred" */
      "src/pages/InvalidInvite"
    )),
  },
  {
    exact: true,
    path: ROUTE_PATH.INVITE_REQUIRED,
    componentLevel: AUTH_LEVEL.DASHBOARD,
    component: lazy(() => import(
      /* webpackChunkName: "core-components-deferred" */
      "src/pages/InviteRequired"
    )),
  },
  {
    exact          : true,
    hasFooter      : true,
    path           : ROUTE_PATH.US,
    componentLevel : AUTH_LEVEL.DASHBOARD,
    component      : lazy(() => import(
      /* webpackPrefetch: true, webpackChunkName: "core-components" */
      "src/pages/Main"
    )),
  },
  {
    exact          : true,
    hasFooter      : true,
    path           : ROUTE_PATH.INTERNATIONAL,
    componentLevel : AUTH_LEVEL.DASHBOARD,
    component      : lazy(() => import(
      /* webpackPrefetch: true, webpackChunkName: "core-components" */
      "src/pages/Main"
    )),
  },
  {
    exact          : true,
    hasFooter      : true,
    path           : ROUTE_PATH.INVEST,
    componentLevel : AUTH_LEVEL.DASHBOARD,
    component      : lazy(() => import(
      /* webpackPrefetch: true, webpackChunkName: "core-components" */
      "src/pages/Main"
    )),
  },
  {
    exact          : true,
    hasFooter      : true,
    path           : ROUTE_PATH.START,
    componentLevel : AUTH_LEVEL.DASHBOARD,
    component      : lazy(() => import(
      /* webpackPrefetch: true, webpackChunkName: "core-components" */
      "src/pages/Start"
    )),
  },
  {
    exact          : true,
    hasFooter      : true,
    path           : ROUTE_PATH.PUBLIC,
    componentLevel : AUTH_LEVEL.PUBLIC,
    component      : lazy(() => import(
      /* webpackPrefetch: true, webpackChunkName: "core-components" */
      "src/pages/Public"
    )),
  },
  {
    exact          : true,
    hasFooter      : true,
    path           : ROUTE_PATH.AUTH_WITH_SERIES,
    componentLevel : AUTH_LEVEL.DASHBOARD,
    component      : lazy(() => import(
      /* webpackPrefetch: true, webpackChunkName: "core-components" */
      "src/pages/AuthWithSeries"
    )),
  },
  {
    exact          : true,
    hasFooter      : true,
    path           : ROUTE_PATH.KYB_FLOW,
    componentLevel : AUTH_LEVEL.DASHBOARD,
    component      : lazy(() => import(
      /* webpackPrefetch: true, webpackChunkName: "core-components" */
      "src/pages/KYBFlow"
    )),
  },
  {
    exact          : true,
    hasFooter      : true,
    path           : ROUTE_PATH.ACCEPT_GRANT,
    componentLevel : AUTH_LEVEL.DASHBOARD,
    component      : lazy(() => import(
      /* webpackChunkName: "core-components-deferred" */
      "src/pages/Grants"
    )),
  },
  {
    exact          : true,
    hasFooter      : true,
    path           : ROUTE_PATH.SYNDICATE,
    componentLevel : AUTH_LEVEL.DASHBOARD,
    component      : lazy(() => import(
      /* webpackChunkName: "core-components-deferred" */
      "src/pages/Syndicates"
    )),
  },
];

const routeViews = config_REACT_APP_STAGE === APP_STAGE.DEV
  ? allRoutes
  : allRoutes.filter(route => !route.devOnly);

const AppRoutes = () => {
  const { context } = useAppContext();
  const { user } = usePrivy();
  const { data: accountInfo } = useAccountInfo();
  const userItemId = get(accountInfo, "userItem.id");
  const location = useLocation();

  // Fairmint UUID (userItem.id) is the business identity for Sentry.
  // Privy user.id is session/auth only — wait until getAccountInfo resolves.
  // Auth preload stays in navigators (series/grants) which already pass userItem.id;
  // calling preloadData({ type: "auth" }) here would only re-fetch getAccountInfo.
  const prevPrivyUserIdRef = useRef(user?.id);
  useEffect(() => {
    const prevPrivyUserId = prevPrivyUserIdRef.current;
    const nextPrivyUserId = user?.id;
    // Clear whenever a prior Privy session departs or changes (covers A→B and
    // A→null outside useLogout). Match logout: revalidate false so public keys
    // are not force-refetched; authenticated hooks refetch when they remount/need data.
    const sessionDepartedOrChanged = Boolean(
      prevPrivyUserId && prevPrivyUserId !== nextPrivyUserId
    );
    if (sessionDepartedOrChanged) {
      clearSessionCaches({ revalidate: false });
    }
    prevPrivyUserIdRef.current = nextPrivyUserId;

    if (!user || !userItemId || sessionDepartedOrChanged) {
      Sentry.setUser(null);
      return;
    }

    const email = get(user, "email.address") || get(user, "google.email");
    Sentry.setUser({
      id: userItemId,
      email,
    });
  }, [user, userItemId]);

  return (
    <main>
      <RouteErrorBoundary key={location.key}>
        <Routes>
          <Route
            path="/404"
            exact
            element={<NotFound />}
          />
          <Route
            path="/sanctioned"
            exact
            element={<Sanctioned />}
          />
          <Route
            path="/upload-documentation"
            exact
            element={<UploadDocument />}
          />
          <Route
            path="/invitation/:invitationCode"
            exact
            element={<InvitationCode />}
          />
          <Route
            path="/"
            exact
            element={<RootRedirect />}
          />
          <Route path="/:type/:slug">
            {routeViews.map(route => {
              const Navigator = navigatorMap[context] || navigatorMap.default;
              return (
                <Route
                  key={route.path}
                  path={route.path}
                  exact={route.exact}
                  componentLevel={route.componentLevel}
                  element={(
                    <Navigator
                      componentLevel={route.componentLevel}
                      permission={route.permission}
                      path={route.path}
                    >
                      <route.component />
                    </Navigator>
                  )}
                />
              );
            })}
          </Route>
          <Route path="*" element={<Navigate to="/404" />} />
        </Routes>
      </RouteErrorBoundary>
    </main>
  );
};

export default AppRoutes;
